import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
import { Toaster } from '@/components/ui/toaster'
import { Toaster as SonnerToaster } from 'sonner'
import { AuthProvider } from '@/contexts/AuthContext'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { AdminRoute, ProtectedRoute } from '@/components/ProtectedRoute'
import Login from './pages/login'
import MissionControl from './pages/mission-control'
import LinkedInInbox from './pages/inbox'
import AIProdavac from './pages/ai-prodavac'
import Settings from './pages/settings'
import CRMDashboard from './pages/crm/index'
import ContactDetail from './pages/contact-detail'
import CampaignBattlefield from './pages/campaign'
import LeadDossier from './pages/lead-dossier'
import MarketingSync from './pages/marketing-sync'
import AcquisitionSignals from './pages/acquisition-signals'
import LeadInbox from './pages/lead-inbox/index'
import Outreach from './pages/outreach/index'
import NotFound from './pages/not-found'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
    },
  },
})

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route 
                  path="/" 
                  element={
                    <ProtectedRoute>
                      <MissionControl />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/inbox" 
                  element={
                    <ProtectedRoute>
                      <LinkedInInbox />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/campaigns/:campaignId" 
                  element={
                    <ProtectedRoute>
                      <CampaignBattlefield />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/leads/:heyreachLeadId" 
                  element={
                    <ProtectedRoute>
                      <LeadDossier />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/ai-prodavac" 
                  element={
                    <ProtectedRoute>
                      <AIProdavac />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/ai-prodavac/:versionId" 
                  element={
                    <ProtectedRoute>
                      <AIProdavac />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/crm" 
                  element={
                    <ProtectedRoute>
                      <CRMDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/crm/contact/:contactId" 
                  element={
                    <ProtectedRoute>
                      <ContactDetail />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/dashboard" 
                  element={<Navigate to="/crm" replace />} 
                />
                <Route 
                  path="/contacts" 
                  element={<Navigate to="/crm" replace />} 
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketing-sync"
                  element={
                    <AdminRoute>
                      <MarketingSync />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/lead-inbox"
                  element={
                    <AdminRoute>
                      <LeadInbox />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/outreach"
                  element={
                    <AdminRoute>
                      <Outreach />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/high-intent"
                  element={
                    <AdminRoute>
                      <AcquisitionSignals />
                    </AdminRoute>
                  }
                />
                {/*
                  Wrapped in ProtectedRoute even though NotFound itself needs no auth data:
                  a signed-out visitor hitting an unknown URL must redirect to
                  /login?returnTo=... like every other route, not render the Sidebar
                  (which calls useAuth()/useLinkedInDraftCounts()) without a session.
                */}
                <Route
                  path="*"
                  element={
                    <ProtectedRoute>
                      <NotFound />
                    </ProtectedRoute>
                  }
                />
              </Routes>
              <Toaster />
              <SonnerToaster 
                position="top-right"
                toastOptions={{
                  className: 'border border-border bg-card text-foreground shadow-elevation-2',
                  descriptionClassName: 'text-muted-foreground',
                }}
                theme="dark"
              />
            </BrowserRouter>
          </AuthProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  )
}

export default App
